import React, { useRef } from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import { array, node, object, string } from 'prop-types';
import { Text } from '@landtrustinc/design-system';
import { ReactComponent as CheckIcon } from '../../assets/icons/trust-check.svg';
import {
  composeValidators,
  maxLength as maxLengthValidator,
  required,
} from '../../util/validators';
import { isFlexiblePricingEnabled } from '../../util/package';
import { FieldDates, FieldPartySizeSelect, FieldTextInput, FlexiblePartySizeSelect } from '..';
import { INQUIRY_MESSAGE_MAX_LENGTH } from './inquiryPackageModel';
import InquiryFieldError, { inquiryFieldErrorsClassName } from './InquiryFieldError';
import InquiryLodgingAddOns from './InquiryLodgingAddOns';
import css from './InquiryTripDetailsForm.module.css';

const InquiryFieldLabel = ({ children }) => (
  <Text as="span" size="sm" fontWeight="semibold" color="var(--color-primary-800)">
    {children}
  </Text>
);

InquiryFieldLabel.propTypes = {
  children: node.isRequired,
};

const InquiryTripDetailsForm = ({
  intl,
  form,
  values,
  selectedPackage,
  dates,
  lodging,
  timeSlots,
  firstName,
  validationErrors,
}) => {
  const fieldDatesContainerRef = useRef(null);
  const { startDate, endDate } = values?.bookingDates || {};
  const bookingDateText =
    dates.bookingDateText || intl.formatMessage({ id: 'InquiryWizard.datesFieldPlaceholder' });

  return (
    <div className={inquiryFieldErrorsClassName}>
      <div className={css.fieldRow}>
        <div className={css.guestsField}>
          {isFlexiblePricingEnabled(selectedPackage.activity) ? (
            <FlexiblePartySizeSelect
              id="party-size-select"
              name="partySize"
              minPartySize={lodging.minParty}
              maxPartySize={lodging.maxParty || lodging.maxDiyPartySize}
              price={lodging.price}
              packageFlexiblePricing={selectedPackage.flexiblePricing}
              validate={composeValidators(
                required(intl.formatMessage({ id: 'InquiryWizard.partySizeRequired' }))
              )}
              readOnlyInput
              label={
                <InquiryFieldLabel>
                  <FormattedMessage id="PricingPage.partySizeLabel" />
                </InquiryFieldLabel>
              }
            />
          ) : (
            <FieldPartySizeSelect
              id="party-size-select"
              name="partySize"
              minPartySize={lodging.minParty || 1}
              maxPartySize={lodging.maxParty || lodging.maxDiyPartySize}
              price={lodging.price}
              parse={value => (value === '' ? null : Number(value))}
              format={value => String(value)}
              validate={required(intl.formatMessage({ id: 'InquiryWizard.partySizeRequired' }))}
              label={
                <InquiryFieldLabel>
                  <FormattedMessage id="PricingPage.partySizeLabel" />
                </InquiryFieldLabel>
              }
            />
          )}
        </div>

        <div className={css.datesField} ref={fieldDatesContainerRef}>
          <FieldDates
            fieldDateProps={{
              endDateOffset:
                lodging.days && lodging.days.min === lodging.days.max
                  ? day => day.add(lodging.days.min - 1, 'days')
                  : undefined,
              minimumNights:
                lodging.days && lodging.days.min !== lodging.days.max
                  ? lodging.days.min - 1
                  : undefined,
              maximumNights:
                lodging.days && lodging.days.max > 0 && lodging.days.min !== lodging.days.max
                  ? lodging.days.max - 1
                  : undefined,
              calendarInfo: lodging.calendarInfoDays ? (
                <FormattedMessage
                  id="PricingPage.datesFieldInfoPackageTitle"
                  values={{ days: lodging.calendarInfoDays }}
                />
              ) : null,
              hasLodging: lodging.hasLodging || lodging.dynamicHasLodging,
              containerRef: fieldDatesContainerRef,
              activity: selectedPackage.activity,
            }}
            timeSlots={timeSlots}
            label={
              <InquiryFieldLabel>
                <FormattedMessage id="InquiryWizard.datesFieldHeadingPackage" />
              </InquiryFieldLabel>
            }
            text={bookingDateText}
            onChange={nextDates => {
              form.change('bookingDates', nextDates);
              form.change('dates', nextDates);
            }}
            isPackage
            placement="bottom-end"
          />
          {startDate && endDate && (
            <div className={css.availableDates}>
              <CheckIcon />
              <Text as="span" size="sm" fontWeight="bold" color="var(--color-primary-800)">
                <FormattedMessage
                  id="InquiryWizard.selectedDate"
                  values={{ dates: dates.formattedStartDate === dates.formattedEndDate ? 1 : 0 }}
                />
              </Text>
            </div>
          )}
          {validationErrors.dates ? (
            <InquiryFieldError data-testid="inquiry-dates-error">
              <FormattedMessage id={validationErrors.dates} />
            </InquiryFieldError>
          ) : null}
        </div>
      </div>

      {lodging.showAddOns ? (
        <InquiryLodgingAddOns
          optionalLodging={lodging.optionalLodging}
          lodgingAddons={values.lodgingAddons}
          dateValues={values.bookingDates}
          availableDays={lodging.availableDays}
          hasLodging={lodging.hasLodging}
          form={form}
        />
      ) : null}

      <div className={css.messageField}>
        <FieldTextInput
          id="message"
          name="message"
          type="textarea"
          label={
            <InquiryFieldLabel>
              {intl.formatMessage(
                { id: 'InquiryWizard.askLandowner' },
                { name: firstName || 'the landowner' }
              )}
            </InquiryFieldLabel>
          }
          maxLength={INQUIRY_MESSAGE_MAX_LENGTH}
          validate={composeValidators(
            required(intl.formatMessage({ id: 'InquiryWizard.messageRequired' })),
            maxLengthValidator(
              intl.formatMessage({ id: 'InquiryWizard.messageTooLong' }),
              INQUIRY_MESSAGE_MAX_LENGTH
            )
          )}
        />
      </div>
    </div>
  );
};

InquiryTripDetailsForm.defaultProps = {
  values: null,
  firstName: '',
  timeSlots: null,
  validationErrors: {},
};

InquiryTripDetailsForm.propTypes = {
  intl: intlShape.isRequired,
  form: object.isRequired,
  values: object,
  selectedPackage: object.isRequired,
  dates: object.isRequired,
  lodging: object.isRequired,
  timeSlots: array,
  firstName: string,
  validationErrors: object,
};

export default InquiryTripDetailsForm;
